import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, ShoppingBag, Leaf, Droplets, Flame } from 'lucide-react';
import { cn } from '@/utils/cn';
import { products as localProducts, categories, getBestsellers, Product } from '@/data/products';
import { useShop } from '@/context/ShopContext';
import { SEO } from '@/components/SEO';
import { ShopStyleProductCard } from '@/components/ShopStyleProductCard';

const categoryIcons: Record<string, React.ReactNode> = {
  skincare: <Droplets className="w-5 h-5" />,
  'body-care': <Leaf className="w-5 h-5" />,
  tea: <ShoppingBag className="w-5 h-5" />,
  candles: <Flame className="w-5 h-5" />,
};

const categoryColors: Record<string, string> = {
  all: '#567D38',
  skincare: '#42C5E7',
  'body-care': '#F4C95D',
  tea: '#BDD630',
  candles: '#E07A5F',
};

export function Shop() {
  const { addToCart } = useShop();
  const [searchParams, setSearchParams] = useSearchParams();
  // Search/category are driven by the URL so that links from the header search,
  // the Home "Shop by Concern" tiles and the footer category links actually land
  // on a filtered shop, and so the filtered view stays shareable/bookmarkable.
  const searchQuery = searchParams.get('search') ?? '';
  const selectedCategory = categories.some(c => c.id === searchParams.get('category'))
    ? searchParams.get('category')!
    : 'all';
  const [isVisible, setIsVisible] = useState(false);

  // Writing filters back into the URL. `replace` keeps typing in the search box
  // from flooding the history stack with one entry per keystroke.
  const updateParam = (key: string, value: string, clearWhen: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === clearWhen) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const setSearchQuery = (value: string) => updateParam('search', value, '');
  const setSelectedCategory = (value: string) => updateParam('category', value, 'all');

  const [addedToCart, setAddedToCart] = useState<string | null>(null);
  const [displayProducts] = useState<Product[]>(localProducts);
  const [sortBy, setSortBy] = useState<'featured' | 'price-asc' | 'price-desc' | 'rating'>('featured');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Filter products
  const filteredProducts = displayProducts
    .filter(product => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.keyIngredients.some(i => i.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;

      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0; // featured — preserve catalogue order
    });

  const bestsellers = getBestsellers();

  const handleAddToCart = (product: Product) => {
    addToCart(product, 1);
    setAddedToCart(product.id);
    setTimeout(() => setAddedToCart(null), 2000);
  };

  return (
    <div className="min-h-screen" style={{ background: '#F7F5EE' }}>
      <SEO
        title="Shop African Botanical Skincare | Marple & Co"
        description="Browse Marple & Co's full collection of handmade African botanical skincare, wellness teas, and aromatherapy candles. Made in South Africa with Kalahari Melon, African Potato, Ximenia Oil and Bee Propolis."
        path="/shop"
        schema={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Marple & Co — African Holistic Skincare Collection",
          "description": "Handmade African botanical skincare, wellness teas and aromatherapy candles.",
          "url": "https://marplecompany.co.za/shop",
          "provider": { "@id": "https://marplecompany.co.za/#organization" }
        }}
      />
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #567D38 0%, #1a3d2a 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="container-marple relative z-10">
          <div 
            className={cn(
              'text-center max-w-3xl mx-auto transition-all duration-700',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'rgba(168, 212, 74, 0.2)' }}
            >
              <Sparkles className="w-4 h-4" style={{ color: '#BDD630' }} />
              <span className="text-sm font-semibold" style={{ color: '#BDD630' }}>Hand-pressed in South Africa</span>
            </div>
            
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Root-to-Radiance<br />
              <span style={{ color: '#c5e47a', fontStyle: 'italic' }}>Collection</span>
            </h1>
            
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Premium skincare, wellness teas, and aromatherapy candles crafted with 
              powerful African botanicals for your holistic wellness journey.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content: sticky sidebar + product grid */}
      <div className="container-marple py-12 grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-7">
        {/* Sidebar */}
        <aside>
          <div
            className="bg-white rounded-2xl p-5 lg:sticky lg:top-[104px]"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
          >
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search the garden…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 rounded-full border-2 text-sm focus:outline-none transition-colors"
                style={{ borderColor: '#e8e2d9', background: 'white' }}
              />
            </div>
            <div
              className="text-xs font-bold uppercase tracking-wider mb-3"
              style={{ color: '#567D38', fontFamily: 'var(--font-display, inherit)' }}
            >
              Shop by Category
            </div>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="flex items-center gap-2 w-full text-left py-2 text-sm transition-colors"
                style={{
                  color: selectedCategory === category.id ? '#567D38' : '#6B7B6E',
                  fontWeight: selectedCategory === category.id ? 600 : 400,
                  borderBottom: '1px solid rgba(217,179,140,.4)',
                }}
              >
                {categoryIcons[category.id]}
                {category.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Products */}
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <span className="text-sm" style={{ color: '#6B7B6E' }}>
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              aria-label="Sort products"
              className="text-sm font-medium rounded-full border-2 px-4 py-2 focus:outline-none"
              style={{ borderColor: '#e8e2d9', color: '#1A2E12', background: 'white' }}
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
              <option value="rating">Top rated</option>
            </select>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredProducts.map((product, index) => (
                <ShopStyleProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  isVisible={isVisible}
                  addedToCart={addedToCart === product.id}
                  onAddToCart={() => handleAddToCart(product)}
                  categoryColor={categoryColors[product.category] || '#567D38'}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4" style={{ color: '#D9B38C' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#1A2E12' }}>No products found</h3>
              <p style={{ color: '#6B7B6E' }}>Try adjusting your search or category filter</p>
            </div>
          )}
        </div>
      </div>

      {/* Bestsellers Section */}
      {bestsellers.length > 0 && selectedCategory === 'all' && !searchQuery && (
        <section className="py-16" style={{ background: 'white' }}>
          <div className="container-marple">
            <div className="text-center mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: '#BDD630' }}>
                The Cult Shelf
              </span>
              <h2 className="font-serif text-3xl font-bold" style={{ color: '#1A2E12' }}>
                THE CULT SHELF
              </h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bestsellers.slice(0, 4).map((product) => (
                <ShopStyleProductCard
                  key={`bestseller-${product.id}`}
                  product={product}
                  index={0}
                  isVisible={true}
                  addedToCart={addedToCart === product.id}
                  onAddToCart={() => handleAddToCart(product)}
                  categoryColor={categoryColors[product.category] || '#567D38'}
                  compact
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

